// @ts-nocheck
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadCurrentBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadCurrentBillTo";
import loadCurrentShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTo";
import { getCurrentShipToState, getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import AddressInfoDisplay from "@insite/content-library/Components/AddressInfoDisplay";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon/Icon";
import Link from "@insite/mobius/Link";
import Popover, { PopoverPresentationProps } from "@insite/mobius/Popover";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ChevronDown from "@store/Library/Components/Toolkit/Icons/ChevronDown";
import PinLine from "@store/Library/Components/Toolkit/Icons/Pin/PinLine";
import { GridContainerProps } from "@store/MobiusOverrides/GridContainer/GridContainer";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import { addressProps } from "@store/Themes/common/props/address";
import usePreviousExisting from "@toolkit/Framework/Common/Hooks/usePreviousExisting";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import getCssProp from "@toolkit/Framework/Common/Utilities/getCssProp";
import { pxToRem, pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { gridGapStyles } from "@toolkit/Themes/common/mixins";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    changeCustomerPageLink: getPageLinkByPageType(state, "ChangeCustomerPage"),
    isSignedIn: state.context.session.isAuthenticated && !getCurrentUserIsGuest(state),
    customerName: getShipToState(state, state.context.session.shipToId).value?.customerName,
    currentBillTo: getCurrentBillToState(state).value,
    currentShipTo: getCurrentShipToState(state).value,
    currentMode: getCookie(NavigationModeCookieName) || "Storefront",
});

const mapDispatchToProps = {
    loadCurrentBillTo,
    loadCurrentShipTo,
};

export interface HeaderSelectedStoreNameStyles {
    signOutWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    vmiTitleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    titleTypographyWrapper?: InjectableCss;
    titleTypographyIcon?: IconPresentationProps;
    popover?: PopoverPresentationProps;
    popoverContent?: InjectableCss;
    customerGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    infoSubtitle?: TypographyPresentationProps;
    billingAddressWrapper?: InjectableCss;
}

export const headerSelectedStoreNameStyles: HeaderSelectedStoreNameStyles = {
    signOutWrapper: {
        css: css`
            display: flex;
            align-items: center;
            margin: ${pxToRem(5)} 0;
            padding-right: ${pxToRem(10)};
            border-right: 1px solid ${getColor("common.border")};
        `,
    },
    titleClickable: {
        css: css`
            &:hover,
            &[aria-expanded="true"] {
                ${IconWrapper} {
                    color: ${getColor("primary")};
                }
            }
        `,
    },
    vmiTitleClickable: {
        css: css`
            &:hover,
            &[aria-expanded="true"] {
                ${IconWrapper} {
                    color: ${getColor("primary.lighter")};
                }
            }
        `,
    },
    titleIcon: {
        size: 22,
        css: css`
            transition: ${transitionHoverStyles()};
            margin-right: ${pxToRem(5)};
        `,
    },
    titleTypographyWrapper: {
        css: css`
            display: flex;
            align-items: center;
            color: ${getColor("primary")};
        `,
    },
    titleTypography: {
        css: css`
            text-transform: none;
            margin-right: ${pxToRem(10)};
        `,
    },
    titleTypographyIcon: {
        src: ChevronDown,
    },
    popover: {
        contentBodyProps: {
            css: css`
                max-height: none;
                color: ${getColor("text")};
                width: ${pxToRem(600)};
                padding: ${pxToRem(25)};
                text-transform: none;
                font-size: ${x => x.theme.design.typography.size.smaller};
                ${getCssProp("box-shadow", "theme.shadows.light60")}

                ${breakpoint("sm", "down")} {
                    width: ${pxToRem(400)};
                }
            `,
        },
    },
    customerGridContainer: {
        gap: 0,
        css: css`
            ${x => gridGapStyles(pxToRemArray([20]), [x.theme.design.typography.spacing.base])}
        `,
    },
    infoGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;
        `,
    },
    infoSubtitle: addressProps.label,
    billingAddressWrapper: {
        css: css`
            height: 100%;
            width: 100%;
            border-right: 1px solid ${getColor("common.border")};

            ${breakpoint("sm", "down")} {
                border-right: 0;
                border-bottom: 1px solid ${getColor("common.border")};
                padding-bottom: ${pxToRem(15)};
            }
        `,
    },
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const HeaderSelectedStoreName = ({
    changeCustomerPageLink,
    isSignedIn,
    customerName,
    currentBillTo,
    currentShipTo,
    loadCurrentBillTo,
    loadCurrentShipTo,
    currentMode,
}: Props) => {
    const lastBillTo = usePreviousExisting(currentBillTo);
    const lastShipTo = usePreviousExisting(currentShipTo);
    const billTo = currentBillTo || lastBillTo;
    const shipTo = currentShipTo || lastShipTo;

    const element = React.useRef<HTMLUListElement>(null);
    const popover = React.useRef<HTMLUListElement>(null);

    useEffect(() => {
        if (isSignedIn) {
            if (!currentBillTo) {
                loadCurrentBillTo();
            }
            if (!currentShipTo) {
                loadCurrentShipTo();
            }
        }
    }, []);

    const styles = useMergeStyles("headerSignIn", headerSelectedStoreNameStyles);

    const closePopover = () => {
        popover.current && (popover.current! as any).closePopover();
    };

    const vmiColor = css`
        color: ${getColor("primary.contrast")};
    `;

    const storeNameButton = (
        <Clickable
            {...(currentMode === "Vmi" ? styles.vmiTitleClickable : styles.titleClickable)}
            data-test-selector="header_signIn"
        >
            <Icon {...styles.titleIcon} src={PinLine} color={currentMode === "Vmi" ? "primary.contrast" : "icon"} />
            <StyledWrapper {...styles.titleTypographyWrapper}>
                <Typography {...styles.titleTypography} css={currentMode === "Vmi" ? vmiColor : undefined}>
                    {customerName}
                </Typography>
                <Icon {...styles.titleTypographyIcon} css={currentMode === "Vmi" ? vmiColor : undefined} />
            </StyledWrapper>
        </Clickable>
    );

    if (!isSignedIn) {
        return <></>;
    }

    return (
        <StyledWrapper {...styles.signOutWrapper}>
            <Popover
                {...styles.popover}
                contentBodyProps={{
                    ...styles.popover?.contentBodyProps,
                    as: "div",
                    ref: element,
                }}
                positionFunction={() => ({
                    position: "absolute",
                    right: 0,
                })}
                ref={popover}
                insideRefs={[element]}
                popoverTrigger={storeNameButton}
            >
                <StyledWrapper {...styles.popoverContent}>
                    <GridContainer {...styles.customerGridContainer}>
                        {billTo && (
                            <GridItem {...styles.infoGridItem}>
                                <StyledWrapper {...styles.billingAddressWrapper}>
                                    <Typography {...styles.infoSubtitle}>{translate("Billing Address")}</Typography>
                                    <AddressInfoDisplay
                                        {...billTo}
                                        state={billTo.state?.abbreviation}
                                        country={billTo.country?.abbreviation}
                                        extendedStyles={styles.address}
                                    />
                                    {changeCustomerPageLink && !changeCustomerPageLink.excludeFromNavigation && (
                                        <Link href={changeCustomerPageLink?.url} onClick={closePopover}>
                                            {changeCustomerPageLink?.title}
                                        </Link>
                                    )}
                                </StyledWrapper>
                            </GridItem>
                        )}
                        {shipTo && (
                            <GridItem {...styles.infoGridItem}>
                                <StyledWrapper>
                                    <Typography {...styles.infoSubtitle}>{translate("Shipping Address")}</Typography>
                                    <AddressInfoDisplay
                                        {...shipTo}
                                        state={shipTo.state?.abbreviation}
                                        country={shipTo.country?.abbreviation}
                                        extendedStyles={styles.address}
                                    />
                                </StyledWrapper>
                            </GridItem>
                        )}
                    </GridContainer>
                </StyledWrapper>
            </Popover>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(HeaderSelectedStoreName),
    definition: {
        displayName: "Header Selected Store Name",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [],
        group: "Common",
    },
};

export default widgetModule;
